import { useEffect, useRef, useState } from "react";
import { motion, animate, useInView } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Gift, Equal } from "lucide-react";
import Reveal from "@/components/site/Reveal";
import { track } from "@/lib/analytics";

/** Animated number that counts up when it enters the viewport. */
function CountUp({ to, duration = 1.8 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!inView) return undefined;
    const controls = animate(0, to, {
      duration,
      ease: [0.22, 1, 0.36, 1],
      onUpdate: (v) => setVal(v),
    });
    return () => controls.stop();
  }, [inView, to, duration]);
  return <span ref={ref}>{Math.round(val).toLocaleString("es-ES")}</span>;
}

export default function CalculatorTeaser() {
  return (
    <section
      id="calculadora"
      data-testid="calculator-teaser-section"
      className="relative bg-[#04120E] text-white pt-16 pb-28 lg:pt-20 lg:pb-40 overflow-hidden grain scroll-mt-24"
    >
      {/* Luz cenital verde + halo dorado */}
      <div aria-hidden className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(ellipse 70% 55% at 50% 0%, rgba(8,80,65,0.55), transparent 65%)" }} />
      <div
        aria-hidden
        className="absolute -top-10 -right-[140px] w-[480px] h-[480px] rounded-full blur-[70px] pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(201,168,76,0.2) 0%, transparent 70%)" }}
      />

      <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          {/* Left: kicker + freebie badge */}
          <Reveal className="lg:col-span-5 text-center">
            <div className="inline-flex flex-wrap items-center justify-center gap-x-3 gap-y-1.5 text-[10px] uppercase tracking-[0.28em] text-[#C9A84C] mb-8 border border-[#C9A84C]/40 bg-[#C9A84C]/[0.07] rounded-full pl-3 pr-5 py-2 badge-pulse-gold" data-testid="calculator-teaser-badge">
              <span className="w-6 h-6 rounded-full bg-[#C9A84C] grid place-items-center">
                <Gift size={11} strokeWidth={2} className="text-[#06231D]" />
              </span>
              <span>Recurso gratuito</span>
              <span aria-hidden className="hidden sm:inline text-[#C9A84C]/60">·</span>
              <span className="basis-full sm:basis-auto">Sin compra</span>
            </div>

            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-medium tracking-tight leading-[1.05] max-w-[20ch] mx-auto">
              ¿Cuánto te cuesta{" "}
              <span className="font-serif-italic text-[#2EC48A] font-normal">NO</span> gestionar bien el talento?
            </h2>

            <p className="mt-8 text-white/70 text-base lg:text-lg leading-relaxed font-light max-w-xl mx-auto text-center">
              Una calculadora rápida que te da una estimación real —en la moneda de tu país— de lo que
              te está costando (o te ha costado) no tener una gestión estructurada del equipo.
            </p>
            <p className="mt-5 text-white/70 text-base lg:text-lg leading-relaxed font-light max-w-xl mx-auto text-center">
              Recibes también un PDF personalizado con recomendaciones accionables.
            </p>
          </Reveal>

          {/* Right: CTA card */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-7 relative"
          >
            <div className="relative">

              {/* Pantalla de la calculadora */}
              <div data-testid="calculator-teaser-display" className="relative rounded-[18px] border border-[#C9A84C]/30 bg-[#FDFBF7] p-6 lg:p-7 mb-5">
                <div className="flex items-center justify-center gap-3 mb-5">
                  <span className="text-[10px] uppercase tracking-[0.24em] text-[#06231D]/50">Coste estimado de rotación</span>
                  <span className="w-2 h-2 rounded-full bg-[#2EC48A] animate-pulse" />
                </div>
                <div className="flex items-baseline justify-center gap-3 flex-wrap">
                  <span className="font-serif-italic text-4xl sm:text-5xl lg:text-6xl leading-none text-[#06231D]" data-testid="calculator-teaser-amount" title="Descúbrelo con la calculadora"><span className="blur-[7px] select-none"><CountUp to={28800} /></span> € <span className="text-[#06231D]/35">·</span> $</span>
                  <span className="text-[11px] uppercase tracking-[0.2em] text-[#06231D]/45">/ año</span>
                </div>
                <div className="mt-5 pt-4 border-t border-[#06231D]/10 text-[12px] text-[#06231D]/60 font-light tracking-wide text-center">
                  El coste oculto que no estás viendo.
                </div>
              </div>

              <div className="mb-10">
                <Link
                  to="/calculadora"
                  data-testid="calculator-teaser-cta"
                  onClick={() => track("cta_click", { location: "calculator_teaser", product: "lead_magnet" })}
                  className="group flex items-center justify-center gap-3 w-full px-8 h-16 lg:h-[4.5rem] rounded-full brand-gradient text-white text-base lg:text-lg font-semibold whitespace-nowrap transition-all duration-300 hover:brightness-105 hover:-translate-y-0.5 hover:shadow-[0_22px_48px_-18px_rgba(46,196,138,0.7)] cta-pulse-emerald"
                >
                  Calcular en 2 minutos
                  <ArrowRight size={18} strokeWidth={1.8} className="group-hover:translate-x-1 transition-transform" />
                </Link>
                <div className="mt-3 text-[11px] uppercase tracking-[0.2em] text-white/60 text-center">
                  Gratis · Sin registro · PDF por email al terminar
                </div>
              </div>

              {/* Teclado */}
              <div data-testid="calculator-teaser-keys" className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 mb-10">
                {[
                  { k: "Equipo", v: "12" },
                  { k: "Salario", v: "2.400 €" },
                  { k: "Bajas/año", v: "3" },
                ].map((key) => (
                  <div key={key.k} className="rounded-[14px] border border-white/10 border-b-[3px] border-b-white/[0.12] bg-white/[0.05] px-2 py-3 text-center">
                    <div className="text-[9px] uppercase tracking-[0.18em] text-white/40 mb-1.5">{key.k}</div>
                    <div className="font-display text-sm sm:text-base lg:text-lg text-white whitespace-nowrap">{key.v}</div>
                  </div>
                ))}
                <div className="rounded-[14px] bg-white border-b-[3px] border-b-black/25 grid place-items-center">
                  <Equal size={18} strokeWidth={2.2} className="text-[#06231D]" />
                </div>
              </div>

              <ul className="relative space-y-4 text-white/80 text-[15px] leading-relaxed font-light w-fit mx-auto">
                <li className="flex items-start gap-3">
                  <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[#C9A84C] flex-shrink-0" />
                  Dos ramas: coste de rotación real o coste de oportunidad
                </li>
                <li className="flex items-start gap-3">
                  <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[#C9A84C] flex-shrink-0" />
                  Resultado inmediato en tu moneda con interpretación práctica
                </li>
                <li className="flex items-start gap-3">
                  <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[#C9A84C] flex-shrink-0" />
                  PDF personalizado por email con recomendaciones accionables
                </li>
              </ul>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
